// Shared filters - used across multiple object types

import {
	createDateRangeFilter,
	createKeywordFilter,
	createRadioFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const sharedFilters: FilterRegistry = {
	salespeople: {
		type: 'multi-select',
		label: 'Salespeople',
		placeholder: 'Search salespeople...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'salesperson_id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
			count: 'client_count' as keyof unknown,
		},
		apiEndpoint: '/users/salespeople',
		queryParamName: 'salespeople',
		multiple: true,
		// PD-14109: count reflects the currently filtered Clients list.
		scopeCountsToView: true,
		options: [
			{ id: 'ME', label: 'Me' },
			{ id: 'null', label: 'Unassigned' },
		],
		queryMapping: {
			objectTable: 'client',
			fieldName: '_fk_user__id',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client', 'itemClientHistory', 'price_contract_client'],
	},

	teams: {
		type: 'multi-select',
		label: 'Teams',
		placeholder: 'Search teams...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		// PD-14207: no count on the report Teams filter. The only value the
		// endpoint could supply is member_count (global team size), which is
		// unrelated to any report's data and was therefore misleading — dropped
		// so this filter matches the (count-less) salespeople/companies filters
		// beside it on reports. Real per-report row→team counts were assessed as
		// low-value/undefinable (see PD-14207) and deferred.
		displayFields: {
			id: 'team_id' as keyof unknown,
			primary: 'team_name' as keyof unknown,
		},
		apiEndpoint: '/teams',
		queryParamName: 'teams',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['reports'],
	},

	// Reports/dashboard "Companies" filter. Hits /users/companies (which
	// inner-joins user_company on the *delegated-as* user) so a salesperson
	// only sees the companies they're linked to — matching what the client
	// list, order list, etc. already do via their own *Company filters.
	// Restriction at the report-data layer is enforced separately by
	// CompanyAccessService; this just keeps the dropdown options honest.
	companies: {
		type: 'multi-select',
		label: 'Companies',
		placeholder: 'Search companies...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		apiEndpoint: '/users/companies',
		queryParamName: 'companies',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['reports'],
	},

	dateRange: {
		...createDateRangeFilter({
			label: 'Date Range',
			objectTable: 'client',
			dateField: '_created_date',
			listKeys: ['client', 'reports'],
		}),
		queryParamName: 'dateRange',
		valueExtractor: {
			property: 'value',
		},
	},

	trend: {
		...createRadioFilter({
			label: 'Trend',
			options: [
				{ id: 'all', label: 'All' },
				{ id: 'increasing', label: 'Increasing' },
				{ id: 'decreasing', label: 'Decreasing' },
				{ id: 'stable', label: 'Stable' },
			],
			defaultValue: { id: 'all', label: 'All' },
			objectTable: 'clientItemHistory',
			fieldName: 'trend',
			listKeys: ['clientItemHistory', 'itemClientHistory'],
		}),
		queryParamName: 'trend',
		placeholder: 'Filter by trend...',
	},

	viewType: {
		type: 'text',
		label: 'View Type',
		placeholder: '',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			primary: 'value' as keyof unknown,
		},
		queryParamName: 'viewType',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['clientItemHistory', 'clientCategoryInsights'],
	},

	listType: {
		type: 'text',
		label: 'List Type',
		placeholder: '',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			primary: 'value' as keyof unknown,
		},
		queryParamName: 'listType',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['clientItemHistory', 'clientCategoryInsights'],
	},

	noteCreatedDateRange: {
		...createDateRangeFilter({
			label: 'Created Date',
			objectTable: 'client_note',
			dateField: '_created_date',
			listKeys: ['client_notes', 'opportunity_notes'],
		}),
		queryParamName: 'noteCreatedDateRange',
	},

	noteCreatedByUser: {
		type: 'multi-select',
		label: 'Created By',
		placeholder: 'Search users...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'workdUserId' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users',
		queryParamName: 'noteCreatedByUser',
		multiple: true,
		queryMapping: {
			objectTable: 'client_note',
			fieldName: '_creator_userId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client_notes', 'opportunity_notes'],
	},

	noteModifiedDateRange: {
		...createDateRangeFilter({
			label: 'Modified Date',
			objectTable: 'client_note',
			dateField: '_modified_date',
			listKeys: ['client_notes', 'opportunity_notes'],
		}),
		queryParamName: 'noteModifiedDateRange',
	},

	noteModifiedByUser: {
		type: 'multi-select',
		label: 'Modified By',
		placeholder: 'Search users...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'workdUserId' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users',
		queryParamName: 'noteModifiedByUser',
		multiple: true,
		queryMapping: {
			objectTable: 'client_note',
			fieldName: '_modified_userId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client_notes', 'opportunity_notes'],
	},

	// Notes can be either CRM-origin (created in the app) or External (imported
	// from an outside system — externalId is populated). Mirrors the SM1
	// "CRM vs ERP" filter, renamed per product request.
	noteOrigin: {
		type: 'multi-select',
		label: 'Origin',
		placeholder: 'Select origin...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'noteOrigin',
		multiple: true,
		options: [
			{ id: 'CRM', label: 'CRM' },
			{ id: 'External', label: 'External' },
		],
		queryMapping: {
			objectTable: 'client_note',
			fieldName: 'externalId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client_notes', 'opportunity_notes'],
	},

	// Issue note filters
	issueNoteCreatedDateRange: {
		...createDateRangeFilter({
			label: 'Created Date',
			objectTable: 'issue_note',
			dateField: 'createdDate',
			listKeys: ['issue_notes'],
		}),
		queryParamName: 'issueNoteCreatedDateRange',
	},

	issueNoteCreatedByUser: {
		type: 'multi-select',
		label: 'Created By',
		placeholder: 'Search users...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'workdUserId' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users',
		queryParamName: 'issueNoteCreatedByUser',
		multiple: true,
		queryMapping: {
			objectTable: 'issue_note',
			fieldName: 'creatorUserId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['issue_notes'],
	},

	issueNoteModifiedDateRange: {
		...createDateRangeFilter({
			label: 'Modified Date',
			objectTable: 'issue_note',
			dateField: 'modifiedDate',
			listKeys: ['issue_notes'],
		}),
		queryParamName: 'issueNoteModifiedDateRange',
	},

	issueNoteModifiedByUser: {
		type: 'multi-select',
		label: 'Modified By',
		placeholder: 'Search users...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'workdUserId' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users',
		queryParamName: 'issueNoteModifiedByUser',
		multiple: true,
		queryMapping: {
			objectTable: 'issue_note',
			fieldName: 'modifiedUserId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['issue_notes'],
	},

	// License note filters
	licenseNoteCreatedDateRange: {
		...createDateRangeFilter({
			label: 'Created Date',
			objectTable: 'license_note',
			dateField: 'createdDate',
			listKeys: ['license_notes'],
		}),
		queryParamName: 'licenseNoteCreatedDateRange',
	},

	licenseNoteCreatedByUser: {
		type: 'multi-select',
		label: 'Created By',
		placeholder: 'Search users...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'workdUserId' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users',
		queryParamName: 'licenseNoteCreatedByUser',
		multiple: true,
		queryMapping: {
			objectTable: 'license_note',
			fieldName: 'creatorUserId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_notes'],
	},

	licenseNoteModifiedDateRange: {
		...createDateRangeFilter({
			label: 'Modified Date',
			objectTable: 'license_note',
			dateField: 'modifiedDate',
			listKeys: ['license_notes'],
		}),
		queryParamName: 'licenseNoteModifiedDateRange',
	},

	licenseNoteModifiedByUser: {
		type: 'multi-select',
		label: 'Modified By',
		placeholder: 'Search users...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'workdUserId' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users',
		queryParamName: 'licenseNoteModifiedByUser',
		multiple: true,
		queryMapping: {
			objectTable: 'license_note',
			fieldName: 'modifiedUserId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_notes'],
	},

	// Sourcing request note filters
	sourcingRequestNoteCreatedDateRange: {
		...createDateRangeFilter({
			label: 'Created Date',
			objectTable: 'sourcing_request_note',
			dateField: 'createdDate',
			listKeys: ['sourcing_request_notes'],
		}),
		queryParamName: 'sourcingRequestNoteCreatedDateRange',
	},

	sourcingRequestNoteCreatedByUser: {
		type: 'multi-select',
		label: 'Created By',
		placeholder: 'Search users...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'workdUserId' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users',
		queryParamName: 'sourcingRequestNoteCreatedByUser',
		multiple: true,
		queryMapping: {
			objectTable: 'sourcing_request_note',
			fieldName: 'creatorUserId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['sourcing_request_notes'],
	},

	sourcingRequestNoteModifiedDateRange: {
		...createDateRangeFilter({
			label: 'Modified Date',
			objectTable: 'sourcing_request_note',
			dateField: 'modifiedDate',
			listKeys: ['sourcing_request_notes'],
		}),
		queryParamName: 'sourcingRequestNoteModifiedDateRange',
	},

	sourcingRequestNoteModifiedByUser: {
		type: 'multi-select',
		label: 'Modified By',
		placeholder: 'Search users...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'workdUserId' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users',
		queryParamName: 'sourcingRequestNoteModifiedByUser',
		multiple: true,
		queryMapping: {
			objectTable: 'sourcing_request_note',
			fieldName: 'modifiedUserId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['sourcing_request_notes'],
	},

	visitDateRange: {
		...createDateRangeFilter({
			label: 'Visit Date',
			objectTable: 'client_visit',
			dateField: 'visitDate',
			listKeys: ['client_visits'],
		}),
		queryParamName: 'visitDateRange',
	},

	visitUser: {
		type: 'multi-select',
		label: 'Created By',
		placeholder: 'Search users...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'workdUserId' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users',
		queryParamName: 'visitUser',
		multiple: true,
		queryMapping: {
			objectTable: 'client_visit',
			fieldName: '_creator_userId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client_visits'],
	},

	userKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by name, email...',
			objectTable: 'user',
			listKeys: ['user', 'group-users'],
			// displayName is not a column on UserEntity — only use actual DB fields
			fulltextFields: ['firstName', 'lastName', 'email'],
		}),
		queryParamName: 'userKeywords',
	},
};
